<template>
  <el-table-column min-width="230">
    <template #header>
      <div class="flex items-center justify-between">
        <span>Email Hash</span>
      </div>
    </template>
    <template #default="scope">
      <div class="flex flex-row items-center relative">
        <div v-if="tableStore.deleting === scope.row._id">
          <PulseLoader />
        </div>
        <div v-else class="flex flex-col w-full">
          <div class="w-full border-red-500">
            <div
              class="truncate"
              :title="
                userStore.settings.useRemoteReviewPage
                  ? userStore.settings.remoteReviewPageUrl +
                    '?reviewId=' +
                    scope.row.emailHash.split('/').pop()
                  : scope.row.emailHash
              "
            >
              {{
                userStore.settings.useRemoteReviewPage
                  ? userStore.settings.remoteReviewPageUrl +
                    "?reviewId=" +
                    scope.row.emailHash.split("/").pop()
                  : scope.row.emailHash
              }}
            </div>
            <div v-if="tableStore.showButtons" class="text-xs text-gray-500">
              <Transition
                enter-active-class="transition duration-200 ease-out"
                enter-from-class="transform opacity-0"
                enter-to-class="transform opacity-100"
                leave-active-class="transition duration-500 ease-in"
                leave-from-class="transform opacity-100"
                leave-to-class="transform opacity-0"
              >
                <div
                  v-if="
                    tableStore.copied[scope.$index] ||
                    tableStore.errored[scope.$index]
                  "
                  class="absolute inset-0 flex items-center justify-center bg-gray-800/75 rounded"
                >
                  <el-text
                    type="success"
                    size="small"
                    v-if="tableStore.copied[scope.$index]"
                    ><div class="truncate text-white">
                      Copied to clipboard
                    </div></el-text
                  >
                  <el-text
                    type="warning"
                    size="small"
                    v-if="tableStore.errored[scope.$index]"
                    ><div class="truncate text-white">
                      Error copying - Try a different browser
                    </div></el-text
                  >
                </div>
              </Transition>
              <div class="flex mt-2 justify-between w-full">
                <div
                  class="p-2 w-1/2 text-gray-300 bg-slate-950 hover:bg-slate-900 cursor-pointer text-center"
                  :class="{
                    'hover:bg-green-400': tableStore.clickedRows[scope.$index],
                  }"
                  @click="handleCopy(scope.$index)"
                >
                  Copy Link
                </div>
                <div
                  v-if="userStore.settings.paymentPortalImageUrl"
                  class="p-2 w-1/2 text-gray-300 bg-green-950 hover:bg-green-900 cursor-pointer text-center"
                  @click="handleCopy(scope.$index, (asHTML = true))"
                >
                  Copy Image
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </el-table-column>
</template>

<script setup>
import { ref } from "vue";
import PulseLoader from "vue-spinner/src/PulseLoader.vue";
import { copyToClipboard } from "@/utils";

import { useTable, useUser, useReviewers } from "@/stores";
const tableStore = useTable();
const userStore = useUser();
const reviewersStore = useReviewers();

const writeClipboard = async (content, type = "text/html") => {
  await navigator.clipboard.write([
    new ClipboardItem({
      [type]: new Blob([content], { type }),
    }),
  ]);
};

async function convertImageToDataURI(imageUrl) {
  return new Promise((resolve, reject) => {
    if (reviewersStore.imageDataURIs[imageUrl]) {
      resolve(reviewersStore.imageDataURIs[imageUrl]);
      return;
    }

    const img = new Image();
    img.crossOrigin = "Anonymous"; // This enables CORS
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const dataURI = canvas.toDataURL("image/png");
      reviewersStore.imageDataURIs[imageUrl] = dataURI;

      resolve(dataURI);
    };
    img.onerror = (error) => {
      reject(error);
    };
    img.src = imageUrl;
  });
}

const handleCopy = async (index, asHTML = false) => {
  try {
    const reviewer = reviewersStore.reviewers[index];
    if (
      reviewer.invoice &&
      (reviewer.invoice.status === "paid" ||
        reviewer.invoice.status === "claimed paid") &&
      reviewer.status === "reviewed"
    ) {
      window.confirm(
        "This link cannot be copied because both payment and review have been completed."
      );
      return;
    }

    tableStore.errored[index] = false;
    tableStore.clickedRows[index] = true;

    const hash = tableStore.sortedTableData[index].emailHash;
    const {
      paymentPortalImageUrl,
      reviewPortalImageUrl,
      useRemoteReviewPage,
      remoteReviewPageUrl,
    } = userStore.settings;

    const justHash = hash.split("/").pop();

    const url = useRemoteReviewPage
      ? `${remoteReviewPageUrl}?reviewId=${justHash}` // Use the remote URL with the hash as a query param
      : hash; // Or just use the hash itself if not using the remote page

    if (asHTML) {
      const invoiceStatus = reviewersStore.reviewers[index].invoice?.status;

      await handleCopyHTML(
        invoiceStatus,
        url,
        paymentPortalImageUrl,
        reviewPortalImageUrl
      );
    } else {
      await copyToClipboard(url);
    }

    tableStore.copied[index] = true;
    resetFlags(index, [tableStore.clickedRows, tableStore.copied]);
  } catch (e) {
    console.error("Error copying link:", e);
    tableStore.errored[index] = true;
    resetFlags(index, [tableStore.clickedRows, tableStore.errored]);
  }
};

const resetFlags = (index, flags) => {
  setTimeout(() => {
    flags.forEach((flag) => (flag[index] = false));
  }, 2000);
};

async function handleCopyHTML(
  invoiceStatus,
  hash,
  payImageUrl,
  reviewImageUrl
) {
  try {
    const payImageDataURI = await convertImageToDataURI(payImageUrl);
    const reviewImageDataURI = await convertImageToDataURI(
      reviewImageUrl || payImageUrl
    );

    const reviewHyperlinkedImage = `<a href="${hash}"><img src="${reviewImageDataURI}" alt="Click here to review us"></a>`;
    const payHyperlinkedImage = `<a href="${hash}"><img src="${payImageDataURI}" alt="Click here to pay"></a>`;

    const content =
      invoiceStatus !== "unpaid" ? reviewHyperlinkedImage : payHyperlinkedImage;
    try {
      await writeClipboard(content);
    } catch (e) {
      throw e;
    }
  } catch (error) {
    console.error("Failed to convert image:", error);
    throw error;
  }
}
</script>

<style scoped>
.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
</style>
